import "./login.scss";

import { autoinject, computedFrom, observable, bindable, bindingMode } from "aurelia-framework";

import { appLogger } from "../../infrastructure/logging";
import { ChallengeService, IPasswordStatusResult } from "./../../services/challenge-service";
import { ExternalAuthService } from "../../services/external-auth-service";
import { SSOType } from "../../enums/sso-type";
import { LoginStage } from "../../enums/login-stage";
import { SecondFactor } from "./second-factor";
import { PasswordForm } from "./password-form";
import { isConsumerLogin } from "../../utilities/is-consumer-login";
import { localGlobal } from "../../infrastructure/infrastructure";
import Infrastructure from "../../infrastructure/infrastructure";

@autoinject
export class LoginPage {
  private _challengeService: ChallengeService;
  private _externalAuthService: ExternalAuthService;
  public returnUrl: string;
  public isLoading: boolean = false;
  public secondFactor?: SecondFactor;
  public passwordForm?: PasswordForm;

  public passwordCheckResponse: IPasswordStatusResult;
  public isTwoFAActive:boolean
  public showSuccessMessage: boolean = false;
  public currentYear: string = "" + new Date().getFullYear();
  public releaseVersion = Infrastructure.releaseVersion;
  public releaseEnvironment = Infrastructure.releaseEnvironment;
  public releaseTimestamp = Infrastructure.releaseTimestamp;
  public buildVersion = Infrastructure.buildVersion;
  @observable
  public stage: LoginStage;

  public constructor(
    challengeService: ChallengeService,
    externalAuthService: ExternalAuthService
  ) {
    this._challengeService = challengeService;
    this._externalAuthService = externalAuthService;
  }

  @computedFrom("returnUrl")
  public get requestValid(): boolean {
    return this.returnUrl != null;
  }

  @computedFrom("stage")
  public get showCopyRightAndIFrame() {
    return (
      this.stage == LoginStage.consumerLogin ||
      this.stage == LoginStage.email ||
      this.stage == LoginStage.password ||
      this.stage == LoginStage.success ||
      this.stage == LoginStage.updatePassword
    );
  }

  public get isIos() {
    return (
      localGlobal._isiOS
    );
  }

  public async activate(params: any) {
    params = params || {};
    this.returnUrl = params.returnUrl;
    if (isConsumerLogin(this.returnUrl)) {
      this.stage = LoginStage.consumerLogin;
      return;
    }
    const ssoType = this._externalAuthService.getSsoType();
    if (!!ssoType) {
      this.handleSso(ssoType);
    } else {
      this.stage = LoginStage.email;
    }
  }

  public async handleSso(ssoType: string) {
    const token = this.getToken();
    const codeParameters = this.getCodeAndState();
    appLogger.info(token);
    appLogger.info(codeParameters.code);
    if (!token && !codeParameters.code) {
      // if token is empty continue with identity flow
      this.stage = LoginStage.email;
      this._externalAuthService.clear();
      return;
    }
    appLogger.info(ssoType);
    if (ssoType === SSOType.okta) {
      appLogger.info("using okta flow");
      await this.handleOkta(codeParameters.code, codeParameters.state);
    }
    if (ssoType === SSOType.oneLogin) {
      await this.handleOneLogin(token);
    }
    if (ssoType === SSOType.azureAD) {
      appLogger.info("using azure flow");
      await this.handleAzureAd(token, codeParameters.code, codeParameters.state);
    }
    if (ssoType === SSOType.googleWorkspace) {
      appLogger.info("using google workspace");
      await this.handleGoogleWorkspace(codeParameters.code as string, codeParameters.state as string);
    }
  }

  public getToken() {
    if (window.location.href.indexOf("#") < 0) {
      return null;
    }
    let hash = window.location.hash;
    let urlParams = new URLSearchParams(hash.replace("#", "?"));
    return urlParams.get("id_token");
  }

  public getCodeAndState() {
    if (window.location.href.indexOf("#") < 0) {
      if(window.location.search != "") {
        let urlParamsQueryString = new URLSearchParams(window.location.search);
        return { code: urlParamsQueryString.get("code"), state: urlParamsQueryString.get("state")};
      }
      return { code: null, state: null };
    }
    let hash = window.location.hash;
    let urlParams = new URLSearchParams(hash.replace("#", "?"));
    return { code: urlParams.get("code"), state: urlParams.get("state")};
  }

  private async handleOneLogin(token: string) {
    this.returnUrl = this._externalAuthService.getSsoReturnUrl();
    let oneLoginTokenValidationSuccess = await this.challengeOneLogin(token);
    if (!oneLoginTokenValidationSuccess) {
      return;
    }
    if (!!this.secondFactor) {
      await this.secondFactor.challengeSecondFactor();
    }
  }

  public async challengeOneLogin(token: string) {
    let result = await this.load(() =>
      this._challengeService.challengeOneLogin(token)
    );
    let failure = result == null;
    if (failure) {
      this.stage = LoginStage.email;
      this._externalAuthService.clear();
      appLogger.warn("OneLogin token validation failed.");
    } else {
      this._externalAuthService.clear();
    }
    return !failure;
  }

  private async handleAzureAd(token: string, code: string, state: string) {
    this.returnUrl = this._externalAuthService.getSsoReturnUrl();
    let azureLoginTokenValidationSuccess = await this.challengeAzureAd(token, code, state);
    if (!azureLoginTokenValidationSuccess) {
      return;
    }
    if (!!this.secondFactor) {
      await this.secondFactor.challengeSecondFactor();
    }
  }

  public async challengeAzureAd(token: string, code: string, state: string) {
    let result = await this.load(() =>
      this._challengeService.challengeAzureAd(token, code, state)
    );
    let failure = result == null;
    if (failure) {
      this.stage = LoginStage.email;
      this._externalAuthService.clear();
      appLogger.warn("AzureAD token validation failed.");
    } else {
      this._externalAuthService.clear();
    }
    return !failure;
  }

  private async handleOkta(code: string, state: string) {
    this.returnUrl = this._externalAuthService.getSsoReturnUrl();
    let azureLoginTokenValidationSuccess = await this.challengeOkta(code, state);
    if (!azureLoginTokenValidationSuccess) {
      return;
    }
    if (!!this.secondFactor) {
      await this.secondFactor.challengeSecondFactor();
    }
  }

  public async challengeOkta(code: string, state: string) {
    let result = await this.load(() =>
      this._challengeService.challengeOkta(code, state)
    );
    let failure = result == null;
    if (failure) {
      this.stage = LoginStage.email;
      this._externalAuthService.clear();
      appLogger.warn("Okta token validation failed.");
    } else {
      this._externalAuthService.clear();
    }
    return !failure;
  }

private async handleGoogleWorkspace(code: string, state: string) {
    this.returnUrl = this._externalAuthService.getSsoReturnUrl();
    let googleLoginTokenValidationSuccess = await this.challengeGoogleWorkspace(code, state);
    if (!googleLoginTokenValidationSuccess) {
      return;
    }
    if (!!this.secondFactor) {
      await this.secondFactor.challengeSecondFactor();
    }
  }

  public async challengeGoogleWorkspace(code: string, state: string) {
    let result = await this.load(() =>
      this._challengeService.challengeGoogleWorkspace(code, state)
    );
    let failure = result == null;
    if (failure) {
      this.stage = LoginStage.email;
      this._externalAuthService.clear();
      appLogger.warn("Google Workspace token validation failed.");
    } else {
      this._externalAuthService.clear();
    }
    return !failure;
  }

  public submitSecondFactor() {
    if (!!this.secondFactor) {
      return this.secondFactor.submitSecondFactor();
    }
    return false;
  }

  public async finalize() {
    if (this.passwordCheckResponse?.isPasswordChangeRequired) {
      this.stage = LoginStage.updatePassword;
      return;
    }

    let identityResult = await this.load(() =>
      this._challengeService.identityChallenge()
    );
    if (this.returnUrl != null && identityResult != null) {    
        this.stage = LoginStage.success;
    } else {
        this.stage = LoginStage.error;
    } 
  }

  private async load<T>(func: () => Promise<T>) {
    this.isLoading = true;
    let result = await func();
    this.isLoading = false;
    return result;
  }

  public stageChanged(newValue: LoginStage, oldValue: LoginStage) {
    appLogger.info(`Moving from stage '${oldValue}' to '${newValue}'.`);
    if (!!this.passwordForm) {
      this.passwordForm.clearPassword();
    }
    if (!!this.secondFactor) {
      this.secondFactor.clearSecondFactorCode();
    }
  }
}
